import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Stethoscope, Video, FileText, ClipboardCheck, PenLine, UserCheck, BadgeCheck } from "lucide-react";
import { WORKFLOW_STEPS } from "../../data/content";
import { Reveal, SectionHeading } from "./Reveal";

const ICONS = { Stethoscope, Video, FileText, ClipboardCheck, PenLine, UserCheck, BadgeCheck };

const Workflow = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.7", "end 0.75"] });
  const line = useSpring(scrollYProgress, { stiffness: 120, damping: 26, restDelta: 0.001 });

  return (
    <section id="how-it-works" className="bg-brand-sky-soft dark:bg-[#0C1338] py-24 md:py-32" data-testid="workflow-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-16">
        <div className="lg:sticky lg:top-32 self-start">
          <SectionHeading
            id="workflow"
            overline="How It Works"
            title={<>From door-open to chart-closed. <span className="text-brand-gradient">In real time.</span></>}
            sub="One encounter. One expert scribe documenting live inside your EHR — designed to have your note ready for review by the end of the encounter."
          />
          <Reveal delay={0.2}>
            <div className="mt-8 inline-flex items-center gap-3 rounded-2xl glass px-5 py-4">
              <BadgeCheck size={20} className="text-brand-navy dark:text-brand-sky" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Real-time documentation. <span className="text-slate-400 font-medium">No separate AI draft-editing workflow.</span>
              </p>
            </div>
          </Reveal>
        </div>
        <div ref={ref} className="relative pl-10">
          <div className="absolute left-[13px] top-2 bottom-2 w-px bg-slate-200 dark:bg-white/10" />
          <motion.div
            className="absolute left-[13px] top-2 bottom-2 w-px bg-gradient-to-b from-brand-navy to-brand-sky origin-top"
            style={{ scaleY: line }}
          />
          <ol className="space-y-10">
            {WORKFLOW_STEPS.map((step, i) => {
              const Icon = ICONS[step.icon];
              return (
                <Reveal key={step.title} delay={i * 0.05}>
                  <li className="relative flex gap-6 group" data-testid={`workflow-step-${i + 1}`}>
                    <div className="absolute -left-10 top-0 w-7 h-7 rounded-full bg-white dark:bg-brand-ink border-2 border-brand-sky flex items-center justify-center shadow-sm">
                      <span className="text-[10px] font-black text-brand-navy dark:text-brand-sky">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="flex-1 rounded-2xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-6 group-hover:shadow-[0_20px_50px_-25px_rgba(42,49,146,0.35)] group-hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300">
                      <div className="flex items-center gap-4 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-brand-sky-soft dark:bg-white/10 flex items-center justify-center shrink-0">
                          <Icon size={18} className="text-brand-navy dark:text-brand-sky" />
                        </div>
                        <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">{step.title}</h3>
                      </div>
                      <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 pl-14">{step.desc}</p>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
export default Workflow;
